// @flow strict
/* eslint-disable ta-code-conventions/has-matching-test */
import React, { useCallback } from 'react';
import { useLocalize } from '@ta/platform.i18n';
import classNames from '@ta/common.classnames';
import Icon from '@ta/core-ui.icon';
import { ResponsiveImage } from '@ta/media.image';
import { type TypeaheadItem } from './types';
import styles from './SearchResult.css';

type Props = {|
  result: TypeaheadItem,

  /**
   * If true, this result has been focused with keyboard navigation and should be rendered in the
   * "hover" state
   */
  selected?: boolean,

  /**
   * Click callback. This does tracking and navigates to the result.
   */
  onClick?: TypeaheadItem => mixed,

  /**
   * Hover callback. This resets the custom keyboard navigation state, since the user is using a
   * pointing device (onMouseHover) or tab navigation (onFocus) to browse results.
   */
  onHover?: (SyntheticMouseEvent<HTMLAnchorElement>) => mixed,
|};

/**
 * A component that renders a single result from the typeahead API
 */
const SearchResult = ({ result, selected = false, onClick = () => {}, onHover }: Props) => {
  const { title, subtitle, image, url, details } = result;

  const click = useCallback(() => onClick(result), [onClick, result]);
  const { localize } = useLocalize();

  return (
    // rescue result
    details.type === 'SEARCH' ? (
      <a
        className={styles.seeAllResults}
        href={url}
        onClick={click}
        onMouseOver={onHover}
        onFocus={onHover}
      >
        {/* See all results for "{query}" */}
        {localize('see_all_results_for', { query: title })}
      </a>
    ) : (
      // non-rescue result
      <a
        className={classNames(styles.searchResult, { [styles.selected]: selected })}
        title={title}
        href={url}
        onClick={click}
        onMouseOver={onHover}
        onFocus={onHover}
      >
        {(details.type === 'LOCATION' || details.type === 'USER') && (
          <div
            className={classNames(styles.imageContainer, {
              [styles.thumbnail]: details.type === 'LOCATION',
              [styles.avatar]: details.type === 'USER',
            })}
          >
            <div className={styles.placeholderIcon}>
              <Icon
                name={details.type === 'LOCATION' ? 'map-pin-fill' : 'member-fill'}
                color="gray1"
                fontSize={20}
              />
            </div>

            {image != null && (
              <div className={styles.image}>
                <ResponsiveImage
                  sources={image}
                  width="42px"
                  height="42px"
                  ssrContainerDimensions={{ clientWidth: 42, clientHeight: 42 }}
                />
              </div>
            )}
          </div>
        )}

        <div className={styles.textContainer}>
          <div
            className={classNames(styles.title, {
              [styles.wrap]: details.type === 'SEARCH_SUGGESTION',
            })}
          >
            {title}

            {details.type === 'USER' && details.verified && (
              <span className={styles.verifiedIcon}>
                <Icon name="verified-checkmark-fill" color="taGreen" fontSize={16} />
              </span>
            )}
          </div>

          {subtitle != null && <div className={styles.subtitle}>{subtitle}</div>}
        </div>
      </a>
    )
  );
};

export default SearchResult;
